import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

class HideableColumnManager {
  // pageName = undefined; // a string
  // allColumnDescriptions = undefined; // an array of descrips
  // hideableColumnIds = undefined; // an array of ids
  // hideableColumnIdVisibleNamePairs = undefined; // an array of { columnId: 'referralType', visibleName: 'Referral Type' } pairs
  // nHideableColumns = 0; // the number of hideable columns

  columnIdToVisibilityMap = {};

  // public
  visibleColumnDescrips = [];

  setPageName(name) {
    this.pageName = name;
  }

  setColumnDescriptions(descrips) {
    this.allColumnDescriptions = descrips;
    this.hideableColumnIds = descrips.filter((descrip) => descrip.hideable).map((descrip) => descrip.id);
    this.nHideableColumns = this.hideableColumnIds.length;
    this.hideableColumnIdVisibleNamePairs = descrips
      .filter((descrip) => descrip.hideable)
      .map((descrip) => {
        return { columnId: descrip.id, visibleName: descrip.label };
      });
    this.orderedHideableColumnIds = this.hideableColumnIds;
  }

  // public
  loadColumnVisibilityFromRedux() {
    // console.log('In loadColumnVisibilityFromRedux()');
    const settingName = 'columnVisibility';
    let prevVisibilitySettings = DA.getUserSetting(settingName); // an array of objects, one for each page with hideable columns
    if (!prevVisibilitySettings) {
      prevVisibilitySettings = [];
    }
    // console.log('prevVisibilitySettings:');
    // console.log(prevVisibilitySettings);
    const settingsForThisPage = prevVisibilitySettings.find((item) => item.pageName === this.pageName);
    // console.log('settingsForThisPage:');
    // console.log(settingsForThisPage);

    // 2021-07-23 We're working on a problem that somehow the array got doubled when stored at logout time
    // So we're patching this by 'uniquifying' the array
    // const reduxColumnArray = settingsForThisPage ? settingsForThisPage.columns : [];
    const prevSettingsColumnArray = settingsForThisPage ? settingsForThisPage.columns : [];
    let unquifiedColumnArray = [];
    for (let i = 0; i < prevSettingsColumnArray.length; i++) {
      const prevSettingsPair = prevSettingsColumnArray[i];
      const columnId = prevSettingsPair.columnId;
      const foundPair = unquifiedColumnArray.find((p) => p.columnId === columnId);
      if (!foundPair) {
        unquifiedColumnArray = [...unquifiedColumnArray, prevSettingsPair];
      }
    }
    const reduxColumnArray = unquifiedColumnArray;
    // console.log('reduxColumnArray:');
    // console.log(reduxColumnArray);
    if (reduxColumnArray.length > 0) {
      this.orderedHideableColumnIds = reduxColumnArray.map((item) => item.columnId);
    }
    this.hideableColumnIds.forEach((id) => {
      const reduxObjForColumn = reduxColumnArray.find((obj) => obj.columnId === id);
      if (reduxObjForColumn) {
        this.setColumnVisible(id, reduxObjForColumn.visible);
      } else {
        this.setColumnVisible(id, this.getDescripForColumnWithId(id).initiallyShown);
        this.orderedHideableColumnIds = [...this.orderedHideableColumnIds, id];
      }
    });

    this.constructColumnDescripArray();
  }

  // public
  constructAndSaveReduxState() {
    const columnSettingsForThisPage = this.orderedHideableColumnIds.map((columnId) => {
      return { columnId, visible: this.columnIsVisible(columnId) };
    });
    // console.log('columnSettingsForThisPage:');
    // console.log(columnSettingsForThisPage);
    // [{columnId: 'leadTemperature', visible: true}, {columnId: 'owner', visible: false}, ...]

    const allColumnSettings = DA.getUserSetting('columnVisibility');
    /*   [
     *     {
     *       pageName: 'leadsPage',
     *       columns: [
     *         {columnId: 'temperature', visible: true},
     *         {columnId: 'message', visible: false},
     *       ],
     *     },
     *     {
     *       pageName: 'prospectsPage',
     *       columns: [
     *         {columnId: 'phone', visible: true},
     *         {columnId: 'leadTemperature', visible: false},
     *         {columnId: 'facilityName', visible: false},
     *         {columnId: 'salesStage', visible: true},
     *         ...
     *       ],
     *     },
     *   ]
     */

    const otherPageSettings = allColumnSettings.filter((pageSetting) => pageSetting.pageName !== this.pageName);
    const thisPageSetting = { pageName: this.pageName, columns: columnSettingsForThisPage };

    const amendedColumnSettings = [...otherPageSettings, thisPageSetting];
    // console.log('amendedColumnSettings:');
    // console.log(amendedColumnSettings);
    ReduxLoader.setUserSetting('columnVisibility', amendedColumnSettings);
  }

  // public
  setVisibilityOfColumnWithLabel(label, visible) {
    const columnId = this.getColumnIdForVisibleName(label);
    this.setColumnVisible(columnId, visible);
    this.constructColumnDescripArray();
  }

  // public
  setOrderedHideableColumnIdsFromItems(permutationOfTextCheckedPairs) {
    this.orderedHideableColumnIds = permutationOfTextCheckedPairs.map((pair) =>
      this.getColumnIdForVisibleName(pair.text)
    );
    this.constructColumnDescripArray();
  }

  // public
  getHideableColumnNamesAndCheckedState() {
    const pairsWithPossibleNulls = this.orderedHideableColumnIds.map((id) => this.nameCheckedPairForColumnId(id));
    const pairs = pairsWithPossibleNulls.filter((pair) => pair !== null);
    return pairs;
  }

  // public
  columnIsVisible(columnId) {
    return !!this.columnIdToVisibilityMap[columnId];
  }

  setColumnVisible(columnId, visible) {
    this.columnIdToVisibilityMap[columnId] = visible;
  }

  getDescripForColumnWithId(id) {
    return this.allColumnDescriptions.find((descrip) => descrip.id === id);
  }

  getAllColumnIds() {
    if (!this.allColumnIds) {
      this.allColumnIds = this.allColumnDescriptions.map((descrip) => descrip.id);
    }
    return this.allColumnIds;
  }

  getColumnIdForVisibleName(visibleName) {
    return this.hideableColumnIdVisibleNamePairs.find((pair) => pair.visibleName === visibleName).columnId;
  }

  getVisibleNameForColumnWithId(columnId) {
    const matchingPair = this.hideableColumnIdVisibleNamePairs.find((pair) => pair.columnId === columnId);
    return matchingPair ? matchingPair.visibleName : null;
  }

  nHideableDescripsVisited = 0;

  constructColumnDescripArray() {
    let visibleColumnDescripsTemp = [];
    this.nHideableDescripsVisited = 0;
    const allColumnIds = this.getAllColumnIds();
    const nColumnIds = allColumnIds.length;
    for (let i = 0; i < nColumnIds; i++) {
      const columnIdAtI = allColumnIds[i];
      if (this.columnIsFixed(columnIdAtI)) {
        const descrip = this.getDescripForColumnWithId(columnIdAtI);
        visibleColumnDescripsTemp = [...visibleColumnDescripsTemp, descrip];
      } else {
        const descrip = this.getNextVisibleHideableDescrip();
        if (descrip) {
          visibleColumnDescripsTemp = [...visibleColumnDescripsTemp, descrip];
        }
      }
    }
    this.visibleColumnDescrips = visibleColumnDescripsTemp;
  }

  columnIsFixed(columnId) {
    const columnDescrip = this.getDescripForColumnWithId(columnId);
    return !columnDescrip.hideable;
  }

  getNextVisibleHideableDescrip() {
    while (this.nHideableDescripsVisited < this.nHideableColumns) {
      if (this.hideableDescripAtIndexIsVisible(this.nHideableDescripsVisited)) {
        const descrip = this.getHideableDescrip(this.nHideableDescripsVisited);
        this.nHideableDescripsVisited++;
        return descrip;
      } else {
        this.nHideableDescripsVisited++;
      }
    }
    return null;
  }

  hideableDescripAtIndexIsVisible(i) {
    const columnId = this.orderedHideableColumnIds[i];
    return this.columnIsVisible(columnId);
  }

  getHideableDescrip(i) {
    const columnId = this.orderedHideableColumnIds[i];
    return this.getDescripForColumnWithId(columnId);
  }

  nameCheckedPairForColumnId(columnId) {
    // => {name: 'Temp', checked: false} or {name: 'Message', checked: true}
    const columnName = this.getVisibleNameForColumnWithId(columnId);
    if (columnName === null) {
      return null;
    }
    const visible = this.columnIsVisible(columnId);
    return { name: columnName, checked: visible };
  }
}

let managers = {};

// public creater/initializer
function createHcmForPageAndDescriptions(pageName, columnDescrips) {
  if (managers.hasOwnProperty(pageName)) {
    return managers[pageName];
  } else {
    const inst = new HideableColumnManager();
    inst.setPageName(pageName);
    inst.setColumnDescriptions(columnDescrips);
    inst.constructColumnDescripArray();
    managers[pageName] = inst;
    return inst;
  }
}

export const clearHideableColumnManagerState = () => {
  managers = {};
};

export default createHcmForPageAndDescriptions;
